import React, { useRef, useState, useEffect } from 'react';
import {
  Video,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  Settings,
  PictureInPicture,
  Rewind,
  FastForward,
} from 'lucide-react';
import './ViewerComponents.css';

interface VideoViewerProps {
  contentUrl: string;
  title: string;
}

export const VideoViewer: React.FC<VideoViewerProps> = ({ contentUrl, title }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [buffered, setBuffered] = useState(0);

  let hideControlsTimeout: NodeJS.Timeout;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateTime = () => setCurrentTime(video.currentTime);
    const updateDuration = () => setDuration(video.duration);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);
    const updateBuffer = () => {
      if (video.buffered.length > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        const percentage = (bufferedEnd / video.duration) * 100;
        setBuffered(percentage);
      }
    };

    video.addEventListener('timeupdate', updateTime);
    video.addEventListener('loadedmetadata', updateDuration);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('progress', updateBuffer);

    return () => {
      video.removeEventListener('timeupdate', updateTime);
      video.removeEventListener('loadedmetadata', updateDuration);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('progress', updateBuffer);
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!videoRef.current) return;

      switch (e.key.toLowerCase()) {
        case ' ':
        case 'k':
          e.preventDefault();
          togglePlay();
          break;
        case 'arrowright':
          skip(10);
          break;
        case 'arrowleft':
          skip(-10);
          break;
        case 'j':
          skip(-10);
          break;
        case 'l':
          skip(10);
          break;
        case 'f':
          toggleFullscreen();
          break;
        case 'm':
          toggleMute();
          break;
        case 'arrowup':
          e.preventDefault();
          changeVolume(0.1);
          break;
        case 'arrowdown':
          e.preventDefault();
          changeVolume(-0.1);
          break;
        case '>':
          changeSpeed(0.25);
          break;
        case '<':
          changeSpeed(-0.25);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [volume, playbackRate]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  const skip = (seconds: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime += seconds;
    }
  };

  const changeSpeed = (delta: number) => {
    const newSpeed = Math.max(0.25, Math.min(2, playbackRate + delta));
    setPlaybackRate(newSpeed);
    if (videoRef.current) {
      videoRef.current.playbackRate = newSpeed;
    }
  };

  const changeVolume = (delta: number) => {
    const newVolume = Math.max(0, Math.min(1, volume + delta));
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      if (newVolume > 0) setIsMuted(false);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      const newMuted = !isMuted;
      setIsMuted(newMuted);
      videoRef.current.muted = newMuted;
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement && containerRef.current) {
      containerRef.current.requestFullscreen();
      setIsFullscreen(true);
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const togglePictureInPicture = async () => {
    if (videoRef.current) {
      try {
        if (document.pictureInPictureElement) {
          await document.exitPictureInPicture();
        } else {
          await videoRef.current.requestPictureInPicture();
        }
      } catch (error) {
        console.error('PiP error:', error);
      }
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (progressBarRef.current && videoRef.current) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      videoRef.current.currentTime = pos * duration;
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);
    clearTimeout(hideControlsTimeout);
    if (isPlaying) {
      hideControlsTimeout = setTimeout(() => setShowControls(false), 3000);
    }
  };

  const formatTime = (time: number) => {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const speedOptions = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

  return (
    <div className="content-viewer content-viewer--video">
      <div className="content-viewer__header">
        <div className="content-viewer__header-left">
          <Video size={20} className="content-viewer__icon" />
          <span className="content-viewer__title">Video Player</span>
        </div>
      </div>

      <div
        ref={containerRef}
        className={`video-player ${showControls ? 'video-player--show-controls' : ''}`}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => isPlaying && setShowControls(false)}
      >
        <video
          ref={videoRef}
          className="s-admin-video-player__video"
          onClick={togglePlay}
        >
          <source src={contentUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Custom Controls */}
        <div className={`video-player__controls ${showControls ? 'video-player__controls--visible' : ''}`}>
          {/* Progress Bar */}
          <div
            ref={progressBarRef}
            className="s-admin-video-player__progress-bar"
            onClick={handleProgressClick}
          >
            <div className="s-admin-video-player__progress-buffered" style={{ width: `${buffered}%` }} />
            <div className="s-admin-video-player__progress-filled" style={{ width: `${(currentTime / duration) * 100}%` }} />
            <div
              className="s-admin-video-player__progress-thumb"
              style={{ left: `${(currentTime / duration) * 100}%` }}
            />
          </div>

          {/* Control Buttons */}
          <div className="s-admin-video-player__control-row">
            <div className="s-admin-video-player__control-left">
              {/* Play/Pause */}
              <button className="s-admin-video-player__btn" onClick={togglePlay}>
                {isPlaying ? <Pause size={20} /> : <Play size={20} />}
              </button>

              {/* Skip Backward */}
              <button className="s-admin-video-player__btn" onClick={() => skip(-10)} title="Rewind 10s (←)">
                <Rewind size={18} />
              </button>

              {/* Skip Forward */}
              <button className="s-admin-video-player__btn" onClick={() => skip(10)} title="Forward 10s (→)">
                <FastForward size={18} />
              </button>

              {/* Volume */}
              <div className="s-admin-video-player__volume-group">
                <button className="s-admin-video-player__btn" onClick={toggleMute}>
                  {isMuted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={isMuted ? 0 : volume}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    setVolume(val);
                    setIsMuted(val === 0);
                    if (videoRef.current) {
                      videoRef.current.volume = val;
                      videoRef.current.muted = val === 0;
                    }
                  }}
                  className="s-admin-video-player__volume-slider"
                />
              </div>

              {/* Time */}
              <span className="s-admin-video-player__time">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>

            <div className="s-admin-video-player__control-right">
              {/* Speed Control */}
              <div className="s-admin-video-player__settings-dropdown">
                <button
                  className="s-admin-video-player__btn"
                  onClick={() => setShowSettings(!showSettings)}
                  title="Playback speed"
                >
                  <Settings size={20} />
                  <span className="s-admin-video-player__speed-label">{playbackRate}x</span>
                </button>
                {showSettings && (
                  <div className="s-admin-video-player__speed-menu">
                    <div className="s-admin-video-player__speed-title">Playback Speed</div>
                    {speedOptions.map((speed) => (
                      <button
                        key={speed}
                        className={`video-player__speed-option ${playbackRate === speed ? 'active' : ''}`}
                        onClick={() => {
                          setPlaybackRate(speed);
                          if (videoRef.current) {
                            videoRef.current.playbackRate = speed;
                          }
                          setShowSettings(false);
                        }}
                      >
                        {speed}x {speed === 1 && '(Normal)'}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Picture in Picture */}
              <button
                className="s-admin-video-player__btn"
                onClick={togglePictureInPicture}
                title="Picture in Picture"
              >
                <PictureInPicture size={20} />
              </button>

              {/* Fullscreen */}
              <button className="s-admin-video-player__btn" onClick={toggleFullscreen} title="Fullscreen (F)">
                {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Center Play Button Overlay */}
        {!isPlaying && (
          <div className="s-admin-video-player__play-overlay" onClick={togglePlay}>
            <div className="s-admin-video-player__play-button">
              <Play size={48} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
